import React, { useState, useRef } from 'react';
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  Box,
  Collapse,
  useMediaQuery,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Grow,
  ListItemIcon,
  Grid,
  Stack,
  Badge,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Person, Search } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigationFunctions } from '../CustomHook';
import templeJson from '../../../../config/temple.json';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useTheme } from '@mui/material/styles';
import { TempleConfigContext } from '../../../../context/TempleConfigContext';
import { UserContext } from '../../../../context/UserContext';
import { useContext } from 'react';
import { useCart } from '../../../../common/hooks/useCartHook';
import { useServicesOutsideCart } from '../../../../common/hooks/useServicesOutsideCartHook';
import { usePersonalServicesCart } from '../../../../common/hooks/usePersonalServiceCart';

const TempleNavbar = () => {
  const { devoteeData, hasToken, currentUrlPath, handleLogout, handleCartClick } = useNavigationFunctions();
  const { cartArr: templeCartItems } = useCart(); // or useCart('temple')

  // Outside temple services cart
  const { cartArr: outsideCartItems } = useServicesOutsideCart(); // or useCart('outside')

  const { cartArr: personalCartItems } = usePersonalServicesCart(); // or useCart('outside')

  const { config } = useContext(TempleConfigContext);
  const { setUser } = useContext(UserContext);
  const [navExpanded, setNavExpanded] = useState(false);
  const isLgDown = useMediaQuery('(max-width: 1200px)');
  const menuItems = templeJson['Header'].menuItems;
  const theme = useTheme();

  const toggleNavExpanded = () => {
    setNavExpanded(!navExpanded);
  };

  // Unified SubDropdown component for both mobile and desktop
  const SubDropdown = ({ item, mobile = false, onClose }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const closeTimerRef = useRef(null);

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleMouseEnter = () => {
      // Clear any existing close timer
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setOpen(true);
    };

    const handleMouseLeave = () => {
      // Set a delay before closing to allow moving between elements
      closeTimerRef.current = setTimeout(() => {
        setOpen(false);
      }, 200);
    };

    const handleClose = (event) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };

    const handleParentClose = (event) => {
      handleClose(event);
      if (onClose) onClose(event);
    };

    // Cleanup timer on unmount
    React.useEffect(() => {
      return () => {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }
      };
    }, []);

    // Mobile version uses Collapse
    if (mobile) {
      return (
        <Box sx={{ width: '100%' }}>
          <Button
            fullWidth
            color="inherit"
            onClick={handleToggle}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            className="mobile-subdropdown-button"
            sx={{
              justifyContent: { xs: 'flex-start', sm: 'left' },
              textAlign: { xs: 'left', sm: 'left' },
              ml: { xs: '26px', sm: '0' }
            }}
          >
            {item.name}
          </Button>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className="mobile-subdropdown-container">
              {item.items.map((subItem, idx) => (
                <Button
                  key={idx}
                  fullWidth
                  color="inherit"
                  href={subItem.url}
                  target={subItem.name === 'Dashboard' ? '_blank' : undefined}
                  rel={subItem.name === 'Dashboard' ? 'noopener noreferrer' : undefined}
                  className="mobile-subdropdown-item"
                  sx={{
                    justifyContent: { xs: 'flex-start', sm: 'left' },
                    textAlign: { xs: 'left', sm: 'left' },
                    ml: { xs: '38px', sm: '0' }
                  }}
                >
                  {subItem.name}
                </Button>
              ))}
            </Box>
          </Collapse>
        </Box>
      );
    } else {
      // Desktop version uses Popper with improved hover handling
      return (
        <>
          <MenuItem
            ref={anchorRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            aria-haspopup="true"
            className="desktop-submenu-item"
          >
            {item.name}
            <ListItemIcon sx={{ marginLeft: 'auto', minWidth: '16px' }}>
              <KeyboardArrowRightIcon fontSize="small" />
            </ListItemIcon>
          </MenuItem>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement="right-start"
            transition
            disablePortal
            sx={{ zIndex: 1301 }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'right-start' ? 'left top' : 'left bottom'
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    minWidth: 180,
                    marginLeft: '0px',
                    position: 'inherit',
                    borderRadius: 0,
                    paddingTop: '2px'
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <MenuList autoFocusItem={open}>
                    {item.items.map((subItem, idx) => (
                      <MenuItem
                        sx={{ margin: 0 }}
                        key={idx}
                        onClick={handleParentClose}
                        component="a"
                        href={subItem.url}
                        target={subItem.name === 'Dashboard' ? '_blank' : undefined}
                        rel={subItem.name === 'Dashboard' ? 'noopener noreferrer' : undefined}
                        className="desktop-subdropdown-item"
                      >
                        {subItem.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      );
    }
  };

  const loginOptions = [
    {
      name: devoteeData?.first_name,
      type: 'dropdown',
      items: [
        {
          name: 'Dashboard',
          url: `${process.env.REACT_APP_ADMIN_URL}auth/bridge?token=${localStorage.getItem('token')}`
        },
        {
          name: 'Profile',
          url: '/profile'
        },
        {
          name: 'Change Password',
          url: '/changepassword'
        },
        {
          name: 'Donation Report',
          url: '/report'
        },
        { name: 'Requests', url: '/servicerequest' },
        {
          name: 'Logout',
          url: '/priests'
        }
      ]
    }
  ];

  // Component for navigation items
  const NavItem = ({ login = false, item, mobile = false }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };

    if (item.type === 'link') {
      return (
        <Button
          color="inherit"
          href={item.url}
          fullWidth={mobile}
          className={mobile ? 'mobile-nav-link' : 'desktop-nav-link'}
          sx={{
            fontWeight: 600,
            padding: '4px 12px',
            justifyContent: { xs: 'flex-start', sm: 'left' },
            textAlign: { xs: 'left', sm: 'left' },
            ml: { xs: '10px', sm: '0' }
          }}
        >
          {item.name}
        </Button>
      );
    } else if (item.type === 'dropdown') {
      if (mobile) {
        return (
          <Box sx={{ width: '100%' }}>
            <Button
              fullWidth
              color="inherit"
              onClick={handleToggle}
              endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              className="mobile-dropdown-button"
              sx={{
                justifyContent: { xs: 'flex-start', sm: 'left' },
                textAlign: { xs: 'left', sm: 'left' },
                pl: { xs: '20px', sm: '0' },
                pr: { xs: '18px', sm: '0' }
              }}
            >
              {login && item?.name === devoteeData?.first_name ? (
                <Tooltip title={item?.name}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      padding: '0 10px',
                      textOverflow: 'unset',
                      textAlign: 'center'
                    }}
                  >
                    {item.name}
                  </Typography>
                </Tooltip>
              ) : (
                item.name
              )}
            </Button>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box className="mobile-dropdown-container">
                {item.items.map((subItem, idx) =>
                  subItem.type === 'sub-Dropdown' ? (
                    <SubDropdown key={idx} item={subItem} mobile={true} />
                  ) : (
                    <Button
                      sx={{
                        fontWeight: 500,
                        fontSize: '13.5px',
                        padding: '4px 18px',
                        justifyContent: { xs: 'flex-start', sm: 'left' },
                        textAlign: { xs: 'left', sm: 'left' },
                        ml: { xs: '18px', sm: '0' }
                      }}
                      key={idx}
                      fullWidth
                      color="inherit"
                      href={subItem.url}
                      target={subItem.name === 'Dashboard' ? '_blank' : undefined}
                      rel={subItem.name === 'Dashboard' ? 'noopener noreferrer' : undefined}
                      className="mobile-dropdown-item"
                    >
                      {subItem.name}
                    </Button>
                  )
                )}
              </Box>
            </Collapse>
          </Box>
        );
      } else {
        return (
          <Box>
            <Button
              ref={anchorRef}
              color="inherit"
              onClick={handleToggle}
              className="desktop-dropdown-button"
              sx={{
                '& .css-1d6wzja-MuiButton-startIcon': {
                  marginRight: '0 !important'
                },
                '& .css-6xugel': { marginRight: '0 !important' },
                fontWeight: 600,
                fontSize: '14px',
                padding: '3px 14px'
              }}
              startIcon={login == true && <Person fontSize="12px"></Person>}
            >
              {login && item?.name === devoteeData?.first_name ? (
                <Tooltip title={item?.name}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '13.5px',
                      padding: '0 3px',
                      paddingTop: '1px',
                      textOverflow: 'unset',
                      width: {
                        xs: '68px',
                        sm: 'auto'
                      }
                    }}
                  >
                    {item.name}
                  </Typography>
                </Tooltip>
              ) : (
                item.name
              )}
            </Button>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="bottom-start"
              transition
              disablePortal
              sx={{ zIndex: 1300 }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom'
                  }}
                >
                  <Paper elevation={3} sx={{ minWidth: 200 }}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList autoFocusItem={open}>
                        {item.items.map((subItem, idx) =>
                          subItem.type === 'sub-Dropdown' ? (
                            <SubDropdown key={idx} item={subItem} mobile={false} onClose={handleClose} />
                          ) : (
                            <MenuItem
                              key={idx}
                              onClick={(e) => {
                                if (subItem.name == 'Logout') {
                                  handleLogout();
                                  setUser(null);
                                  handleClose(e);
                                } else {
                                  handleClose(e);
                                }
                              }}
                              component="a"
                              href={subItem.name == 'Logout' ? '' : subItem.url}
                              target={subItem.name === 'Dashboard' ? '_blank' : undefined}
                              rel={subItem.name === 'Dashboard' ? 'noopener noreferrer' : undefined}
                              className="desktop-dropdown-item"
                            >
                              {subItem.name}
                            </MenuItem>
                          )
                        )}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        );
      }
    }
    return null;
  };

  return (
    <AppBar
      color="default"
      elevation={2}
      sx={{
        position: 'relative',
        // overflow: 'hidden',
        backgroundColor: templeJson['Header'].style === 1 ? 'var(--primaryWhite) !important' : 'var(--primaryMain) !important'
      }}
    >
      <Grid
        container
        direction="row"
        sx={{
          position: 'relative',
          zIndex: 9999999,
          justifyContent: 'center',
          alignItems: 'center',
          height: templeJson['Header'].style === 1 ? { xs: 'auto', sm: 'auto', md: 'auto' } : { xs: '38px', sm: '38px', md: 'auto' }
        }}
      >
        <Grid item xs={2} sm={2} md={2} lg={2} className="d-flex justify-content-center ">
          {isLgDown ? (
            <IconButton
              aria-label="toggle navigation"
              onClick={toggleNavExpanded}
              edge="start"
              sx={{ color: templeJson['Header'].style === 1 ? '#000!important' : '#fff!important', padding: { xs: '5px', sm: '6px' } }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            templeJson['Header'].style == 1 && (
              <img
                src={`${process.env.REACT_APP_S3_BASE_URL}/api/images/logo.png`}
                alt="logo"
                style={{
                  height: '80px',
                  width: 'auto',
                  paddingTop: '5px',
                  paddingBottom: '5px'
                }}
              />
            )
          )}
        </Grid>

        <Grid item xs={3} sm={6} md={7} lg={7}>
          {!isLgDown ? (
            <Box className="d-flex justify-content-center align-items-center">
              {menuItems.map((item, idx) => (
                <NavItem key={idx} item={item} />
              ))}
            </Box>
          ) : (
            <Box className="d-flex justify-content-center align-items-center">
              {templeJson['Header'].style == 1 && (
                <img
                  src={`${process.env.REACT_APP_S3_BASE_URL}/api/images/logo.png`}
                  alt="logo"
                  style={{ height: window.innerWidth < 600 ? '40px' : '60px' }}
                />
              )}
            </Box>
          )}
        </Grid>
        <Grid item xs={7} sm={4} md={3} lg={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid
            container
            spacing={1}
            alignItems="center"
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-end', md: 'flex-start' }
            }}
          >
            <Grid
              item
              xs="auto"
              sx={{
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {hasToken ? (
                loginOptions.map((item, idx) => <NavItem key={idx} item={item} login={true} />)
              ) : (
                <Button
                  color="inherit"
                  href={`/login?redirect=${encodeURIComponent(currentUrlPath)}`}
                  className="desktop-dropdown-button"
                  sx={{
                    color: templeJson['Header'].style === 1 ? '#000!important' : '#fff!important',
                    letterSpacing: 0.5,
                    '& .css-1d6wzja-MuiButton-startIcon': { marginRight: 0 },
                    fontSize: { xs: '11px', sm: '13px', md: '15px', p: 0 }
                  }}
                  startIcon={<Person />}
                >
                  Login
                </Button>
              )}
            </Grid>

            <Grid
              item
              xs="auto"
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Stack direction="row" alignItems="center">
                <IconButton onClick={handleCartClick}>
                  <Badge
                    badgeContent={templeCartItems.length + outsideCartItems.length + personalCartItems.length}
                    overlap="rectangular"
                    sx={{
                      color: templeJson['Header'].style === 1 ? 'var(--primaryLight)' : '#ffd707!important',
                      marginRight: { xs: '12px', md: 0 }
                    }}
                  >
                    <ShoppingCartIcon color={templeJson['Header']?.style === 1 ? 'black' : 'white'} />
                  </Badge>
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {isLgDown && (
            <Collapse in={navExpanded} timeout="auto" unmountOnExit>
              <Box className="mobile-nav-container nav-container">
                {menuItems.map((item, idx) => (
                  <NavItem key={idx} item={item} mobile={true} />
                ))}
              </Box>
            </Collapse>
          )}
        </Grid>
      </Grid>
    </AppBar>
  );
};

export default TempleNavbar;
